import {AfterViewInit, ChangeDetectorRef, Component, ElementRef, Injectable, OnInit, ViewChild} from '@angular/core';

import {SelectionModel} from '@angular/cdk/collections';
import {FlatTreeControl} from '@angular/cdk/tree';
import {MatTreeFlatDataSource, MatTreeFlattener} from '@angular/material/tree';
import {BehaviorSubject} from 'rxjs';
import {GUID} from "../../../classes/utilities/guid";
import {ModalDirective} from "ngx-bootstrap/modal";
import {MatDialog, MatDialogConfig} from "@angular/material/dialog";
import {ProcessConfigComponent} from "../process-config/process-config.component";
import {ProcessBuilderService} from "../../../services/process-builder.service";
import {BooleanTO} from "../../../classes/boolean-to";
import {ProcessBuilderTO} from "../../../classes/process-builder-to";
import {FormTO} from "../../../classes/form-to";
import {Process} from "../../../classes/process";
import {ProcessFlatNode} from "../../../classes/process-flat-node";
import {ProcessLite} from "../../../classes/process-lite";
import {ProcessType} from "../../../classes/process-type";

/**
 * Checklist database, it can build a tree structured Json object.
 * Each node in Json object represents a to-do item or a category.
 * If a node is a category, it has children items and new items can be added under the category.
 */
@Injectable()
export class ProcessDatabase {
  dataChange = new BehaviorSubject<Process[]>([]);

  treeData: Process[];
  processTypes: ProcessType[];

  get data(): Process[] { return this.dataChange.value; }

  constructor(private processBuilderService: ProcessBuilderService) {
    this.initialize();
  }

  initialize() {
    this.processBuilderService.getProcessTypes().subscribe(processTypes => {
      this.processTypes = processTypes;

      const processRoot = new Process();

      this.processTypes.forEach(processType => {
        if (processType.code == 'FORM_OUTPUT') {
          // console.log('ASSIGNING processRoot.processType ');
          processRoot.key = GUID.newGuid();
          processRoot.name = 'Sprout Form Output';
          processRoot.description = 'Raw Output from Form Submission';
          processRoot.processType = processType;
          processRoot.model = '';
          processRoot.publicOutputInd = false;
          processRoot.children = [];
        }
      });

      let data = [processRoot];

      if (this.processBuilderService.form) {
        const formTO = new FormTO();
        formTO.id = this.processBuilderService.form.id;

        this.processBuilderService.getFormProcesses(formTO).subscribe(response => {
          const processBuilderTO = response as ProcessBuilderTO;

          if (processBuilderTO && processBuilderTO.model) {
            const model: any = JSON.parse(processBuilderTO.model);

            let processModel = model as Process[];

            if (processModel && processModel.length > 0) {
              data = processModel;
            }

          }
          this.dataChange.next(data);

        });

      } else {
        this.dataChange.next(data);
      }
    });
  }

  /** Add an item to process list */
  insertItem(parent: Process, name: string): Process {
    if (!parent.children) {
      parent.children = [];
    }
    const newProcess = { name: name } as Process;

    newProcess.key = GUID.newGuid();
    newProcess.name = 'TODO: node.name';
    newProcess.description = 'TODO: node.description ==> key: ' + newProcess.key;
    newProcess.model = '';

    //978 newProcess.type = ProcessType["JSON Transformation"];

    parent.children.push(newProcess);
    this.dataChange.next(this.data);
    return newProcess;
  }

  insertProcess(parent: Process, process: Process): Process {
    if (!parent.children) {
      parent.children = [];
    }

    parent.children.push(process);
    this.dataChange.next(this.data);
    return process;
  }

  updateProcess(node: Process, process: Process): Process {
    // node.name = process.name;
    node.name = process.name;
    node.description = process.description;
    node.processType = process.processType;
    node.model = process.model;
    node.publicOutputInd = process.publicOutputInd;
    node.outputName = process.outputName;
    node.propertySets = process.propertySets;

    this.dataChange.next(this.data);
    return process;
  }

  insertItemAbove(node: Process, name: string): Process {
    const parentNode = this.getParentFromNodes(node);
    const newItem = { name: name } as Process;
    if (parentNode != null) {
      parentNode.children.splice(parentNode.children.indexOf(node), 0, newItem);
    } else {
      this.data.splice(this.data.indexOf(node), 0, newItem);
    }
    this.dataChange.next(this.data);
    return newItem;
  }

  insertItemBelow(node: Process, name: string): Process {
    const parentNode = this.getParentFromNodes(node);
    const newItem = { name: name } as Process;
    if (parentNode != null) {
      parentNode.children.splice(parentNode.children.indexOf(node) + 1, 0, newItem);
    } else {
      this.data.splice(this.data.indexOf(node) + 1, 0, newItem);
    }
    this.dataChange.next(this.data);
    return newItem;
  }

  getParentFromNodes(node: Process): Process {
    for (let i = 0; i < this.data.length; ++i) {
      const currentRoot = this.data[i];
      const  parent = this.getParent(currentRoot, node);
      if (parent != null) {
        return parent;
      }
    }
    return null;
  }

  getParent(currentRoot: Process, node: Process): Process {
    if (currentRoot.children && currentRoot.children.length > 0) {
      for (let i = 0; i < currentRoot.children.length; ++i) {
        const child = currentRoot.children[i];
        if (child === node) {
          return currentRoot;
        } else if (child.children && child.children.length > 0) {
          const parent = this.getParent(child, node);
          if (parent != null) {
            return parent;
          }
        }
      }
    }
    return null;
  }

  updateItem(node: Process, name: string) {
    node.name = name;
    this.dataChange.next(this.data);
  }

  deleteItem(node: Process) {
    this.deleteNode(this.data, node);
    this.dataChange.next(this.data);
  }

  copyPasteItem(from: Process, to: Process): Process {
    const newItem = this.insertItem(to, from.name);
    if (from.children) {
      from.children.forEach(child => {
        this.copyPasteItem(child, newItem);
      });
    }
    return newItem;
  }

  copyPasteItemAbove(from: Process, to: Process): Process {
    const newItem = this.insertItemAbove(to, from.name);
    if (from.children) {
      from.children.forEach(child => {
        this.copyPasteItem(child, newItem);
      });
    }
    return newItem;
  }

  copyPasteItemBelow(from: Process, to: Process): Process {
    const newItem = this.insertItemBelow(to, from.name);
    if (from.children) {
      from.children.forEach(child => {
        this.copyPasteItem(child, newItem);
      });
    }
    return newItem;
  }

  deleteNode(nodes: Process[], nodeToDelete: Process) {
    const index = nodes.map(function(process: Process) { return process.key; }).indexOf(nodeToDelete.key);
    if (index > -1) {
      nodes.splice(index, 1);
    } else {
      nodes.forEach(node => {
        if (node.children && node.children.length > 0) {
          this.deleteNode(node.children, nodeToDelete);
        }
      });
    }
  }
}

export interface DialogData {
  animal: string;
  name: string;
}

@Component({
  selector: 'app-process-builder',
  templateUrl: './process-builder.component.html',
  styleUrls: ['./process-builder.component.css'],
  providers: [ProcessDatabase]
})
export class ProcessBuilderComponent implements OnInit, AfterViewInit {

  @ViewChild('addProcessModal') public addProcessModal: ModalDirective;

  transformationNode: ProcessFlatNode;

  ngOnInit(): void {
    // console.log('processBuilderService.ngOnInit.form');
    // if (this.processBuilderService.form) {
      // console.dir(this.processBuilderService.form.formName);
    // } else {
    //   console.error('this.processBuilderService.form is undefined.');
    // }
  }

  /** Map from flat node to nested node. This helps us finding the nested node to be modified */
  flatNodeMap = new Map<ProcessFlatNode, Process>();

  /** Map from nested node to flattened node. This helps us to keep the same object for selection */
  nestedNodeMap = new Map<Process, ProcessFlatNode>();

  /** A selected parent node to be inserted */
  selectedParent: ProcessFlatNode | null = null;

  /** The new item's name */
  newItemName = '';

  treeControl: FlatTreeControl<ProcessFlatNode>;

  treeFlattener: MatTreeFlattener<Process, ProcessFlatNode>;

  dataSource: MatTreeFlatDataSource<Process, ProcessFlatNode>;

  /** The selection for checklist */
  checklistSelection = new SelectionModel<ProcessFlatNode>(true /* multiple */);

  /* Drag and drop */
  dragNode: any;
  dragNodeExpandOverWaitTimeMs = 300;
  dragNodeExpandOverNode: any;
  dragNodeExpandOverTime: number;
  dragNodeExpandOverArea: string;
  @ViewChild('emptyItem') emptyItem: ElementRef;

  initializedInd = false;

  constructor(private database: ProcessDatabase, private processBuilderService: ProcessBuilderService, public dialog: MatDialog, private changeDetectorRef: ChangeDetectorRef) {
    this.treeFlattener = new MatTreeFlattener(this.transformer, this.getLevel, this.isExpandable, this.getChildren);
    this.treeControl = new FlatTreeControl<ProcessFlatNode>(this.getLevel, this.isExpandable);
    this.dataSource = new MatTreeFlatDataSource(this.treeControl, this.treeFlattener);

    database.dataChange.subscribe(data => {
      // console.log('dataChange');
      this.dataSource.data = [];
      this.dataSource.data = data;

      if (this.processBuilderService.form) {
        const processBuilderTO = new ProcessBuilderTO();
        processBuilderTO.formId = this.processBuilderService.form.id;
        processBuilderTO.model = JSON.stringify(data);
        processBuilderTO.workingOutputModel = '';

        if (this.initializedInd) {
          this.saveProcessBuilder(processBuilderTO);
        }

      }

      // console.log('about to expand all');
      
      if (!this.initializedInd) {
          setTimeout(() => {
            // console.log('expanding all...');
            this.treeControl.expandAll();
          }, 200);
        this.initializedInd = true;
      }

    });

    processBuilderService.processBuilderHome$.subscribe(processBuilderHome => {
      this.mode = 'tree';
    });

  }

  private saveProcessBuilder(processBuilderTO: ProcessBuilderTO) {
    this.processBuilderService.saveFormProcess(processBuilderTO).subscribe(data => {
      const booleanTO = data as BooleanTO;

      // console.log('saveFormProcess.booleanTO:');
      // console.dir(booleanTO);

    });

  }

  ngAfterViewInit() {
    this.treeControl.expandAll();

  }

  getLevel = (node: ProcessFlatNode) => node.level;

  isExpandable = (node: ProcessFlatNode) => node.expandable;

  getChildren = (node: Process): Process[] => node.children;

  hasChild = (_: number, _nodeData: ProcessFlatNode) => _nodeData.expandable;

  hasNoContent = (_: number, _nodeData: ProcessFlatNode) => _nodeData.name === '';

  /**
   * Transformer to convert nested node to flat node. Record the nodes in maps for later use.
   */
  transformer = (node: Process, level: number) => {
    const existingNode = this.nestedNodeMap.get(node);
    const flatNode = existingNode && existingNode.name === node.name
        ? existingNode
        : new ProcessFlatNode();
    flatNode.key = node.key;
    // flatNode.parentNode =

    flatNode.name = node.name;
    flatNode.level = level;
    flatNode.description = node.description;
    flatNode.model = node.model;

    // console.log('transformer.node:');
    // console.dir(node);
    
    flatNode.processType = node.processType;

    flatNode.outputName = node.outputName;
    flatNode.publicOutputInd = node.publicOutputInd;
    flatNode.propertySets = node.propertySets;
    flatNode.template = node.template;
    flatNode.templateId = node.templateId;

    // console.log('transformer.node.type: ');
    // console.dir(node.type);

    if (node.processType && node.processType.code) {
      switch (node.processType.code) {
        case 'FORM_OUTPUT':
          flatNode.icon = 'input';
          break;
        default:
          flatNode.icon = 'settings';
          break;
      }
    } else {
      flatNode.icon = 'settings';
    }
    flatNode.expandable = (node.children && node.children.length > 0);
    this.flatNodeMap.set(flatNode, node);
    this.nestedNodeMap.set(node, flatNode);
    return flatNode;
  }
  // ProcessType = ProcessType;
  mode = 'tree';
  // mode = 'HANDLEBARS';

  /** Whether all the descendants of the node are selected */
  descendantsAllSelected(node: ProcessFlatNode): boolean {
    const descendants = this.treeControl.getDescendants(node);
    return descendants.every(child => this.checklistSelection.isSelected(child));
  }

  /** Whether part of the descendants are selected */
  descendantsPartiallySelected(node: ProcessFlatNode): boolean {
    const descendants = this.treeControl.getDescendants(node);
    const result = descendants.some(child => this.checklistSelection.isSelected(child));
    return result && !this.descendantsAllSelected(node);
  }

  /** Toggle the to-do item selection. Select/deselect all the descendants node */
  todoItemSelectionToggle(node: ProcessFlatNode): void {
    this.checklistSelection.toggle(node);
    const descendants = this.treeControl.getDescendants(node);
    this.checklistSelection.isSelected(node)
        ? this.checklistSelection.select(...descendants)
        : this.checklistSelection.deselect(...descendants);
  }

  /** Select the category so we can insert the new item. */
  addStep(node: ProcessFlatNode) {
    const dialogConfig = new MatDialogConfig();

    dialogConfig.disableClose = true;
    dialogConfig.autoFocus = true;
    dialogConfig.width = '400px';

    const processLite = new ProcessLite();
    processLite.processTypes = this.database.processTypes;

    dialogConfig.data = processLite;

    // this.dialog.open(ProcessConfigComponent, dialogConfig);

    const dialogRef = this.dialog.open(ProcessConfigComponent, dialogConfig);

    dialogRef.afterClosed().subscribe(
        data => {
          console.log("Dialog output:", data)

          if (data && data.name) {
            const parentNode = this.flatNodeMap.get(node);

            const processTmp = data as Process;

            processTmp.key = GUID.newGuid();
            processTmp.processType = data.processType;

            this.database.insertProcess(parentNode, processTmp);
            this.treeControl.expand(node);
          }
        }
    );
  }

  /** Save the node to database */
  saveNode(node: ProcessFlatNode, itemValue: string) {
    const nestedNode = this.flatNodeMap.get(node);
    this.database.updateItem(nestedNode, itemValue);
  }

  handleDragStart(event, node) {
    // Required by Firefox (https://stackoverflow.com/questions/19055264/why-doesnt-html5-drag-and-drop-work-in-firefox)
    event.dataTransfer.setData('foo', 'bar');
    event.dataTransfer.setDragImage(this.emptyItem.nativeElement, 0, 0);
    this.dragNode = node;
    this.treeControl.collapse(node);
  }

  handleDragOver(event, node) {
    event.preventDefault();

    // Handle node expand
    if (node === this.dragNodeExpandOverNode) {
      if (this.dragNode !== node && !this.treeControl.isExpanded(node)) {
        if ((new Date().getTime() - this.dragNodeExpandOverTime) > this.dragNodeExpandOverWaitTimeMs) {
          this.treeControl.expand(node);
        }
      }
    } else {
      this.dragNodeExpandOverNode = node;
      this.dragNodeExpandOverTime = new Date().getTime();
    }

    // Handle drag area
    const percentageX = event.offsetX / event.target.clientWidth;
    const percentageY = event.offsetY / event.target.clientHeight;
    if (percentageY < 0.25) {
      this.dragNodeExpandOverArea = 'above';
    } else if (percentageY > 0.75) {
      this.dragNodeExpandOverArea = 'below';
    } else {
      this.dragNodeExpandOverArea = 'center';
    }
  }

  handleDrop(event, node) {
    event.preventDefault();
    if (node !== this.dragNode) {
      let newItem: Process;
      if (this.dragNodeExpandOverArea === 'above') {
        newItem = this.database.copyPasteItemAbove(this.flatNodeMap.get(this.dragNode), this.flatNodeMap.get(node));
      } else if (this.dragNodeExpandOverArea === 'below') {
        newItem = this.database.copyPasteItemBelow(this.flatNodeMap.get(this.dragNode), this.flatNodeMap.get(node));
      } else {
        newItem = this.database.copyPasteItem(this.flatNodeMap.get(this.dragNode), this.flatNodeMap.get(node));
      }
      this.database.deleteItem(this.flatNodeMap.get(this.dragNode));
      this.treeControl.expandDescendants(this.nestedNodeMap.get(newItem));
    }
    this.dragNode = null;
    this.dragNodeExpandOverNode = null;
    this.dragNodeExpandOverTime = 0;
  }

  handleDragEnd(event) {
    this.dragNode = null;
    this.dragNodeExpandOverNode = null;
    this.dragNodeExpandOverTime = 0;
  }

  deleteStep(node: any) {
    console.log('deleteStep');
    // const parentNode = this.flatNodeMap.get(node);
    this.database.deleteItem(node);
    // this.treeControl.collapse(node);

  }

  editStep(node: any) {
    // console.log('editStep');

    const processFlatNode = node as ProcessFlatNode;

    const dialogConfig = new MatDialogConfig();

    dialogConfig.disableClose = true;
    dialogConfig.autoFocus = true;
    dialogConfig.width = '400px';

    const processLite = new ProcessLite();
    processLite.processTypes = this.database.processTypes;
    processLite.key = processFlatNode.key;
    processLite.name = processFlatNode.name;
    processLite.description = processFlatNode.description;
    processLite.processType = processFlatNode.processType;
    processLite.model = processFlatNode.model;
    processLite.outputName = processFlatNode.outputName;
    processLite.propertySets = processFlatNode.propertySets ? processFlatNode.propertySets : [];
    processLite.publicOutputInd = processFlatNode.publicOutputInd;

    dialogConfig.data = processLite;

    const dialogRef = this.dialog.open(ProcessConfigComponent, dialogConfig);

    dialogRef.afterClosed().subscribe(
        data => {
          console.log("Dialog output:", data)

          if (data && data.name) {
            // const parentNode = this.flatNodeMap.get(node);
            // const parentNode = this.getParentFromNodes(node);
            const parentNode = this.flatNodeMap.get(node);


            const processTmp = data as Process;

            processTmp.processType = data.processType;

            this.database.updateProcess(parentNode, processTmp);
            // this.treeControl.expand(node);
          }
        }
    );
  }

  transformStep(node: ProcessFlatNode) {
    console.log('transformStep');
    console.dir(node);
    node.formId = this.processBuilderService.form.id;
    this.transformationNode = node;
    if (node.processType && node.processType.code) {
      switch (node.processType.code) {
        case 'FORM_OUTPUT':
          this.processBuilderService.processNode = node;
          this.mode = node.processType.code;
          break;
        case 'HANDLEBARS':
          this.processBuilderService.processNode = node;
          this.mode = node.processType.code;
          break;
        case 'JOLT':
          this.processBuilderService.processNode = node;
          this.mode = node.processType.code;
          break;
        case 'TRANSLATIONS':
          this.processBuilderService.processNode = node;
          this.mode = node.processType.code;
          break;
        case 'HTML2PDF':
          this.processBuilderService.processNode = node;
          this.mode = node.processType.code;
          break;
      }
      this.processBuilderService.processBuilderMode = node.processType;
    }
  }

  onTemplateChange(template: string) {
    // console.log('onTemplateChange123');
    // console.dir(template);
    this.transformationNode.template = template;
    this.transformationNode.formId = this.processBuilderService.form.id;

    // console.dir(this.transformationNode);
    // console.table(this.transformationNode);

    this.processBuilderService.saveTemplate(this.transformationNode).subscribe(response => {
      // console.log('%c response.', 'background: #222; color: #bada55');
      // console.table(response);
    });

  }

  onFormChange($event: string) {
    // console.log('onFormChange');
    // console.table($event);
    const processBuilderTO = new ProcessBuilderTO();
    processBuilderTO.formId = this.processBuilderService.form.id;
    processBuilderTO.workingOutputModel = JSON.stringify($event);

    if (this.initializedInd) {
      // this.saveProcessBuilder(processBuilderTO);
      this.processBuilderService.saveFormProcessOutputModel(processBuilderTO).subscribe(data => {
        const booleanTO = data as BooleanTO;

        // console.log('saveFormProcess.booleanTO:');
        // console.dir(booleanTO);

      });

    }

  }
}
